import { sample } from 'effector';
import { fetchFilesFx } from '@nx-frontend/shared/store/feature-files';
import {
  addMenuItemEvent,
  deleteMenuItemEvent,
  moveMenuItemEvent,
  updateMenuItemEvent,
  updateMenuItemSlugEvent,
  updateMenuItemTypeEvent,
  updateMenuItemVisibilityEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { $portalId } from '@nx-frontend/shared/store/portal-app';
import {
  addBlockEvent,
  deleteBlockEvent,
  pushBlockDataEvent,
  updateBlockActionsEvent,
  updateBlockEvent,
  updateBlockIndexEvent,
  updateBlockPlaceEvent,
  updateBlockSizeEvent,
  updateBlockVisibilityEvent,
} from '@nx-frontend/shared/store/portal-blocks';
import {
  changePageVisibilityEvent,
  ChangePageVisibilityPayload,
  setPageFx,
  updatePageEvent,
  updatePagePathBySlugEvent,
  UpdatePagePayload,
  updatePageTitleByPathEvent,
} from '@nx-frontend/shared/store/portal-page';
import {
  updateAccessSettingsEvent,
  updateAIAssistantSettingsEvent,
  updateClientSettingsEvent,
  updateCustomCodeSettingsEvent,
  updateDisableBrandingEvent,
  updateHeroSettingEvent,
  updateMetaSettingsEvent,
  updatePortalAnalyticsSettingsEvent,
  updateRequestAccessPageEvent,
  updateSearchAnalyticsSettingsEvent,
  updateSearchIndexSettingsEvent,
} from '@nx-frontend/shared/store/portal-settings';
import { updateThemeEvent } from '@nx-frontend/shared/store/portal-theme';
import { addVirtualNode, deleteVirtualNode } from '@nx-frontend/shared/store/virtual-portal-node';
import {
  BlockActionsData,
  BlockDataData,
  BlockIndexData,
  BlockPlaceData,
  BlockSizeData,
  BlockUpdateData,
  BlockVisibilityData,
  ChangeEvent,
  ChangeEventType,
  FilesRecentBlock,
  isDashboardMenuType,
  MenuItemAddPayload,
  MenuItemChangeTypePayload,
  MenuItemChangeVisibilityPayload,
  MenuItemDeletePayload,
  MenuItemMovePayload,
  MenuItemUpdatePayload,
  MenuItemUpdateSlugPayload,
  PortalBlock,
  PortalBlockType,
  PortalSettings,
  PortalThemeConfigPartial,
  UpdateMenuItemSlugEvent,
  VirtualPortalNode,
} from '@nx-frontend/shared/types';
import { $currentChangeEvent, CurrentChangeEvent } from './change-events-store';

const sampleFn = <T>({ undo, event: { newValue, oldValue } }: CurrentChangeEvent): T => {
  return (!undo ? newValue : oldValue) as T;
};

const sampleFnReverse = <T>({ undo, event: { newValue, oldValue } }: CurrentChangeEvent): T => {
  return (!undo ? oldValue : newValue) as T;
};

const isProperPath = (currentPath: string, eventPath: string, history?: ChangeEvent[]) => {
  const urlParams = new URLSearchParams(window.location.search);
  const originalUrl = urlParams.get('originalurl');
  if (currentPath === eventPath || originalUrl === eventPath) {
    return true;
  }
  if (history) {
    const updateSlugEvents = history
      .filter((event) => event.type === ChangeEventType.UPDATE_MENU_ITEM_SLUG)
      .reverse() as UpdateMenuItemSlugEvent[];
    return updateSlugEvents.some(
      (event) => currentPath.endsWith(event.newValue.item.slug) && eventPath.endsWith(event.oldValue.item.slug)
    );
  }
  return false;
};

export const changeEventsRouter = () => {
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo }) =>
      undo === false ? event.type === ChangeEventType.ADD_BLOCK : event.type === ChangeEventType.DELETE_BLOCK,
    fn: (event) => {
      return {
        ...sampleFn<PortalBlock>(event),
        external: !isProperPath(event.path, event.event.meta.path, event.initHistory),
      };
    },
    target: addBlockEvent,
  });
  sample({
    source: [$currentChangeEvent, $portalId],
    filter: ([changeEvent]) => {
      const { event, undo, path, initHistory } = changeEvent as CurrentChangeEvent;
      return (
        ((!undo && event.type === ChangeEventType.ADD_BLOCK) ||
          (undo && event.type === ChangeEventType.DELETE_BLOCK)) &&
        isProperPath(path, event.meta.path, initHistory) &&
        event.newValue?.type === PortalBlockType.FILES_RECENT
      );
    },
    fn: ([changeEvent, portalId]) => {
      const block = (changeEvent as CurrentChangeEvent).event.newValue as FilesRecentBlock;
      return {
        portalId: portalId as string,
        limitSize: block.limit.size,
        limitFrom: block.limit.from,
        sortField: block.sort.field,
        sortOrder: block.sort.order,
      };
    },
    target: fetchFilesFx,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo, path, initHistory }) =>
      (undo === false ? event.type === ChangeEventType.DELETE_BLOCK : event.type === ChangeEventType.ADD_BLOCK) &&
      isProperPath(path, event.meta.path, initHistory),
    fn: (event) => ({
      ...sampleFnReverse<PortalBlock>(event),
      external: !isProperPath(event.path, event.event.meta.path, event.initHistory),
    }),
    target: deleteBlockEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK,
    fn: sampleFn<BlockUpdateData>,
    target: updateBlockEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK_INDEX,
    fn: sampleFn<BlockIndexData>,
    target: updateBlockIndexEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK_ACTIONS,
    fn: sampleFn<BlockActionsData>,
    target: updateBlockActionsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK_SIZE,
    fn: sampleFn<BlockSizeData>,
    target: updateBlockSizeEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK_VISIBILITY,
    fn: sampleFn<BlockVisibilityData>,
    target: updateBlockVisibilityEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BLOCK_PLACE,
    fn: sampleFn<BlockPlaceData>,
    target: updateBlockPlaceEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.PUSH_BLOCK_DATA,
    fn: sampleFn<BlockDataData>,
    target: pushBlockDataEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_MENU_ITEM,
    fn: sampleFn<MenuItemUpdatePayload>,
    target: updateMenuItemEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo }) =>
      undo === false ? event.type === ChangeEventType.ADD_MENU_ITEM : event.type === ChangeEventType.DELETE_MENU_ITEM,
    fn: (event) => {
      const eventPayload = sampleFn<MenuItemAddPayload>(event);
      return { ...eventPayload, soft: true };
    },
    target: addMenuItemEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo }) =>
      undo === false ? event.type === ChangeEventType.DELETE_MENU_ITEM : event.type === ChangeEventType.ADD_MENU_ITEM,
    fn: (event) => {
      const eventPayload = sampleFnReverse<MenuItemDeletePayload>(event);
      return { ...eventPayload, soft: true };
    },
    target: deleteMenuItemEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_MENU_ITEM,
    fn: sampleFn<MenuItemUpdatePayload>,
    target: updateMenuItemEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) =>
      event.type === ChangeEventType.UPDATE_MENU_ITEM && !isDashboardMenuType(event.newValue.item.type),
    fn: (event) => {
      const eventPayload = sampleFn<MenuItemUpdatePayload>(event);
      return { title: eventPayload.item.name, path: eventPayload.item.url };
    },
    target: updatePageTitleByPathEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.CHANGE_MENU_ITEM_VISIBILITY,
    fn: sampleFn<MenuItemChangeVisibilityPayload>,
    target: updateMenuItemVisibilityEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.CHANGE_MENU_ITEM_TYPE,
    fn: sampleFn<MenuItemChangeTypePayload>,
    target: updateMenuItemTypeEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_MENU_ITEM_SLUG,
    fn: sampleFn<MenuItemUpdateSlugPayload>,
    target: updateMenuItemSlugEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_MENU_ITEM_SLUG,
    fn: (event) => {
      const eventPayload = sampleFn<MenuItemUpdateSlugPayload>(event);
      return { id: eventPayload.item.pageId ?? '', slug: eventPayload.item.slug };
    },
    target: updatePagePathBySlugEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.MOVE_MENU_ITEM,
    fn: sampleFn<MenuItemMovePayload>,
    target: moveMenuItemEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo }) =>
      undo === false ? event.type === ChangeEventType.ADD_NODE : event.type === ChangeEventType.DELETE_NODE,
    fn: sampleFn<VirtualPortalNode>,
    target: [addVirtualNode, setPageFx],
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event, undo }) =>
      undo === false ? event.type === ChangeEventType.DELETE_NODE : event.type === ChangeEventType.ADD_NODE,
    fn: sampleFnReverse<VirtualPortalNode>,
    target: deleteVirtualNode,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_PAGE,
    fn: sampleFn<UpdatePagePayload>,
    target: updatePageEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.CHANGE_PAGE_VISIBILITY,
    fn: sampleFn<ChangePageVisibilityPayload>,
    target: changePageVisibilityEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_THEME_SETTINGS,
    fn: sampleFn<PortalThemeConfigPartial>,
    target: updateThemeEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_SEARCH_ANALYTICS_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'searchAnalyticsEnabled'>>,
    target: updateSearchAnalyticsSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_PORTAL_ANALYTICS_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'portalAnalyticsEnabled'>>,
    target: updatePortalAnalyticsSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_SEARCH_INDEX_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'searchIndexEnabled'>>,
    target: updateSearchIndexSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_BRANDING_SETTING,
    fn: sampleFn<Pick<PortalSettings, 'disableBranding'>>,
    target: updateDisableBrandingEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_ACCESS_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'access'>>,
    target: updateAccessSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_CLIENT_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'clientSettings'>>,
    target: updateClientSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_REQUEST_ACCESS_PAGE,
    fn: sampleFn<Pick<PortalSettings, 'requestAccessPage'>>,
    target: updateRequestAccessPageEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_CUSTOM_CODE_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'customCode'>>,
    target: updateCustomCodeSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_META_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'name' | 'description'>>,
    target: updateMetaSettingsEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_HERO_SETTING,
    fn: sampleFn<Pick<PortalSettings, 'searchEnabled' | 'greetingEnabled' | 'greetingMessage' | 'headerBlocksEnabled'>>,
    target: updateHeroSettingEvent,
  });
  sample({
    source: $currentChangeEvent,
    filter: ({ event }) => event.type === ChangeEventType.UPDATE_AI_ASSISTANT_SETTINGS,
    fn: sampleFn<Pick<PortalSettings, 'aiAssistantSettings'>>,
    target: updateAIAssistantSettingsEvent,
  });
};
